import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { reportErrorExternally } from 'shared/core';
import { HELPER_CLIENT_APP_CONFIG } from '../config/helper-client-app-config';
import { AuthDebugEventLogService } from '../debug/auth-debug-event-log.service';

export class AuthenticationRenewalError extends Error {
  constructor(
    readonly status: number,
    renewalError: unknown,
  ) {
    super(
      `Unable to renew token after ${status}: ${
        renewalError instanceof Error ? renewalError.message : renewalError
      }`,
    );
  }
}

@Injectable()
export class AuthenticationTokenInterceptorService implements HttpInterceptor {
  private readonly _secondsBeforeRenew = 5;
  private readonly appConfig = inject(HELPER_CLIENT_APP_CONFIG);
  private readonly authDebugEventLog = inject(AuthDebugEventLogService);
  constructor(private readonly authService: AuthenticationService) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Only intercept calls to our backend
    if (!req.url.startsWith(this.appConfig.apiPath)) {
      return next.handle(req);
    }

    return this.authService.getUser().pipe(
      switchMap((user) => {
        if (
          user &&
          user.access_token &&
          !user.expired &&
          (user.expires_in ?? 0) > this._secondsBeforeRenew
        ) {
          return this.callApi(req, next, user.access_token);
        } else if (user) {
          return from(this.authService.renewToken()).pipe(
            switchMap((user) => {
              return this.callApi(req, next, user.access_token);
            }),
            catchError((error) => {
              return throwError(
                () =>
                  new Error(
                    `Can't acquire new token: ${this.getErrorMessage(error)}`,
                  ),
              );
            }),
          );
        }

        return next.handle(req);
      }),
      catchError((err) => {
        reportErrorExternally(
          new Error(
            `Error while intercepting ${req.method} ${this.getPathname(req.url)}: ${this.getErrorMessage(err)}`,
          ),
        );
        return throwError(() => err);
      }),
    );
  }

  private callApi(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    accessToken: string,
  ): Observable<HttpEvent<unknown>> {
    return next.handle(this.cloneRequestWithToken(req, accessToken)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          this.authDebugEventLog.record(
            'api_auth_failure',
            `API request returned ${error.status}.`,
            {
              method: req.method,
              path: this.getPathname(req.url),
              status: error.status,
              statusText: error.statusText,
            },
          );

          return from(this.authService.renewToken()).pipe(
            catchError((renewError) => {
              return throwError(
                () => new AuthenticationRenewalError(error.status, renewError),
              );
            }),
            switchMap((user) => {
              return next.handle(
                this.cloneRequestWithToken(req, user.access_token),
              );
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  }

  private cloneRequestWithToken(
    req: HttpRequest<unknown>,
    accessToken: string,
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const status = `HTTP ${error.status} ${error.statusText}`.trim();
      const body: unknown = error.error;
      return body === null || body === undefined || body === ''
        ? status
        : `${status}: ${this.getErrorMessage(body)}`;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'object' && error !== null) {
      try {
        return JSON.stringify(error) ?? 'Unknown error';
      } catch {
        return 'Unable to serialize error response';
      }
    }

    return String(error);
  }

  private getPathname(url: string): string {
    let pathname: string;
    try {
      pathname = new URL(url).pathname;
    } catch {
      pathname = url.split('?')[0];
    }
    return pathname.replace(
      /\/video-appointment-launches\/[^/]+\/consume$/,
      '/video-appointment-launches/[redacted]/consume',
    );
  }
}
